import React, { useState, useEffect } from 'react';
import {
    FiSearch,
    FiCheck,
    FiX,
    FiFileText,
    FiCheckSquare,
    FiHelpCircle,
    FiCalendar,
    FiHeadphones,
    FiDownload,
    FiUser,
    FiMail,
    FiClock,
    FiPaperclip,
} from 'react-icons/fi';
import ChatModal from '../user/ChatModal';
import { trustCenterApi } from '../../services/api';

const TYPE_MAP = {
    document:   'document',
    documents:  'document',
    assessment: 'assessment',
    query:      'query',
};

const normalizeRequest = (item) => {
    const rawType = (item.requestType || item.type || 'query').toLowerCase();
    const type = TYPE_MAP[rawType] || 'query';
    return {
        id:           item.id,
        type,
        title:        item.title || item.queryAbout || item.message || 'Untitled',
        date:         item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : item.date || '',
        requester:    item.fullName || item.requester || '',
        email:        item.email || '',
        organization: item.organization || '',
        status:       item.status || 'Pending',
        message:      item.message || '',
        attachments:  item.attachments ?? [],
    };
};

const TYPE_META = {
    document: {
        label: 'Document',
        icon: FiFileText,
        accent: '#0077b5',
        accentBg: 'rgba(0, 119, 181, 0.1)',
        pillBg: 'rgba(0, 119, 181, 0.1)',
        pillText: '#0077b5',
    },
    assessment: {
        label: 'Assessment',
        icon: FiCheckSquare,
        accent: '#005f8e',
        accentBg: 'rgba(0, 95, 142, 0.12)',
        pillBg: 'rgba(0, 95, 142, 0.1)',
        pillText: '#005f8e',
    },
    query: {
        label: 'Query',
        icon: FiHelpCircle,
        accent: '#F47F2E',
        accentBg: 'rgba(244, 127, 46, 0.12)',
        pillBg: 'rgba(244, 127, 46, 0.12)',
        pillText: '#F47F2E',
    },
};

const AdminRequests = () => {
    const [activeTab, setActiveTab] = useState('all');
    const [search, setSearch] = useState('');
    const [requests, setRequests] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [chatRequest, setChatRequest] = useState(null);
    const [actionLoading, setActionLoading] = useState(new Set());
    const [toasts, setToasts] = useState([]);

    // Load list on mount
    useEffect(() => {
        setListLoading(true);
        trustCenterApi
            .getRequests(1, 100)
            .then((res) => {
                const items = Array.isArray(res) ? res : res?.data ?? [];
                setRequests(items.map(normalizeRequest));
            })
            .catch(() => setRequests([]))
            .finally(() => setListLoading(false));
    }, []);

    // Computed tab counts from live data
    const tabs = [
        { id: 'all',         label: 'All',         count: requests.length },
        { id: 'documents',   label: 'Documents',   count: requests.filter((r) => r.type === 'document').length },
        { id: 'assessments', label: 'Assessments', count: requests.filter((r) => r.type === 'assessment').length },
        { id: 'queries',     label: 'Queries',     count: requests.filter((r) => r.type === 'query').length },
    ];

    const openChat = (req) => {
        setSelected(null);
        setChatRequest(req);
    };

    const handleRowClick = (row) => setSelected(row);

    const filtered = requests.filter((r) => {
        const matchTab =
            activeTab === 'all' ||
            (activeTab === 'documents'   && r.type === 'document') ||
            (activeTab === 'assessments' && r.type === 'assessment') ||
            (activeTab === 'queries'     && r.type === 'query');
        const matchSearch = r.title.toLowerCase().includes(search.toLowerCase());
        return matchTab && matchSearch;
    });

    const closeModal = () => setSelected(null);

    const showToast = (message, type = 'success') => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, message, type }]);
        setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
    };

    const handleStatusChange = async (req, newStatus) => {
        setActionLoading((prev) => new Set(prev).add(req.id));
        try {
            const response = await trustCenterApi.updateStatus(req.id, newStatus);
            
            setRequests((prev) =>
                prev.map((r) => r.id === req.id ? { ...r, status: newStatus } : r)
            );
            setSelected((prev) =>
                prev && prev.id === req.id ? null : prev
            );
            
            // Check if email was sent (backend may return emailSent flag)
            const emailSent = response?.emailSent !== false;
            
            if (emailSent) {
                showToast(
                    newStatus === 'Accepted'
                        ? `Request accepted and ${req.email} has been notified.`
                        : `Request rejected and ${req.email} has been notified.`,
                    'success'
                );
            } else {
                // Status updated but email failed
                showToast(
                    newStatus === 'Accepted'
                        ? 'Request accepted, but email notification failed.'
                        : 'Request rejected, but email notification failed.',
                    'warning'
                );
            }
        } catch (error) {
            showToast('Failed to update status. Please try again.', 'error');
        } finally {
            setActionLoading((prev) => {
                const next = new Set(prev);
                next.delete(req.id);
                return next;
            });
        }
    };

    return (
        <div className=" bg-[#919191]/5 p-6">
            <div>
                {/* Top bar: tabs + search */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
                    <div className="flex flex-wrap items-center gap-2">
                        {tabs.map((tab) => {
                            const active = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all border ${active
                                        ? 'bg-[#0077b5] text-white border-[#0077b5] shadow-sm'
                                        : 'bg-white text-[#53545a] border-[#919191]/30 hover:border-[#919191]/60'
                                        }`}
                                >
                                    {tab.label}
                                    <span
                                        className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold ${active
                                            ? 'bg-[#005f8e] text-white'
                                            : 'bg-[#919191]/15 text-[#53545a]'
                                            }`}
                                    >
                                        {tab.count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="relative w-full lg:w-80">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#919191]" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search requests..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[#919191]/30 bg-white text-sm text-[#53545a] placeholder-[#919191] focus:outline-none focus:border-[#0077b5] focus:ring-2 focus:ring-[#0077b5]/15"
                        />
                    </div>
                </div>

                {/* Request rows */}
                <div className="space-y-3">
                    {listLoading && (
                        <div className="bg-white rounded-xl border border-[#919191]/15 py-16 text-center text-[#919191] text-sm">
                            Loading requests…
                        </div>
                    )}
                    {!listLoading && filtered.map((req) => {
                        const meta = TYPE_META[req.type];
                        const Icon = meta.icon;
                        return (
                            <div
                                key={req.id}
                                onClick={() => handleRowClick(req)}
                                className="relative bg-white rounded-xl shadow-sm border border-[#919191]/15 pl-6 pr-5 py-4 flex items-center gap-4 overflow-hidden cursor-pointer transition-all hover:shadow-md hover:border-[#0077b5]/40"
                            >
                                {/* Left accent bar */}
                                <span
                                    className="absolute left-0 top-3 bottom-3 w-1 rounded-r"
                                    style={{ background: meta.accent }}
                                ></span>

                                {/* Icon */}
                                <div
                                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{ background: meta.accentBg }}
                                >
                                    <Icon style={{ color: meta.accent }} className="text-xl" />
                                </div>

                                {/* Title + date */}
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-[#53545a] text-base truncate">
                                        {req.title}
                                    </p>
                                    <div className="flex items-center gap-1.5 text-xs text-[#919191] mt-1">
                                        <FiCalendar className="text-sm" />
                                        <span>{req.date}</span>
                                    </div>
                                </div>

                                {/* Type pill */}
                                <span
                                    className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0"
                                    style={{ background: meta.pillBg, color: meta.pillText }}
                                >
                                    {meta.label}
                                </span>

                                {/* Action buttons */}
                                <div
                                    className="flex items-center gap-2 flex-shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {req.type === 'query' ? (
                                        <>
                                            {/* <button
                                                onClick={() => openChat(req)}
                                                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg border border-[#F47F2E] text-[#F47F2E] text-sm font-semibold hover:bg-[#F47F2E]/10 transition-colors"
                                            >
                                                <FiHeadphones className="text-base" />
                                                Reply
                                            </button> */}
                                           
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => handleStatusChange(req, 'Accepted')}
                                                disabled={actionLoading.has(req.id) || req.status === 'Accepted' || req.status === 'Rejected'}
                                                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg border border-[#0077b5] text-[#0077b5] text-sm font-semibold hover:bg-[#0077b5]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <FiCheck className="text-base" />
                                                Accept
                                            </button>
                                            <button
                                                onClick={() => handleStatusChange(req, 'Rejected')}
                                                disabled={actionLoading.has(req.id) || req.status === 'Accepted' || req.status === 'Rejected'}
                                                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg border border-[#F47F2E] text-[#F47F2E] text-sm font-semibold hover:bg-[#F47F2E]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <FiX className="text-base" />
                                                Reject
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {!listLoading && filtered.length === 0 && (
                        <div className="bg-white rounded-xl border border-[#919191]/15 py-16 text-center text-[#919191] text-sm">
                            No requests match your filter.
                        </div>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            {selected && (() => {
                const meta = TYPE_META[selected.type];
                const Icon = meta.icon;
                const isQuery = selected.type === 'query';
                return (
                    <div className="fixed inset-0 z-50">
                        <div
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                            onClick={closeModal}
                        ></div>

                        <div className="relative flex min-h-full items-center justify-center p-4">
                            <div
                                className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div
                                    className="relative px-6 py-5 text-white"
                                    style={{
                                        background:
                                            'linear-gradient(135deg, #0077b5 0%, #005f8e 100%)',
                                    }}
                                >
                                    <button
                                        onClick={closeModal}
                                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-md border border-white/25 text-white/85 hover:bg-white/10 transition-colors"
                                        aria-label="Close"
                                    >
                                        <FiX className="text-lg" />
                                    </button>
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                                            style={{ background: 'rgba(255,255,255,0.18)' }}
                                        >
                                            <Icon className="text-xl text-white" />
                                        </div>
                                        <div>
                                            <span
                                                className="inline-block text-xs font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full mb-1"
                                                style={{
                                                    background: 'rgba(244,127,46,0.18)',
                                                    color: '#F47F2E',
                                                }}
                                            >
                                                {meta.label}
                                            </span>
                                            <h2 className="text-xl font-bold leading-tight">
                                                {selected.title}
                                            </h2>
                                        </div>
                                    </div>
                                </div>

                                {/* Body */}
                                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                                    {/* Meta grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="bg-[#919191]/5 border border-[#919191]/15 rounded-lg px-4 py-3">
                                            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-[#919191] mb-1">
                                                <FiUser /> Requester
                                            </div>
                                            <p className="text-sm font-semibold text-[#53545a]">
                                                {selected.requester}
                                            </p>
                                            <p className="text-xs text-[#919191]">
                                                {selected.organization}
                                            </p>
                                        </div>
                                        <div className="bg-[#919191]/5 border border-[#919191]/15 rounded-lg px-4 py-3">
                                            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-[#919191] mb-1">
                                                <FiMail /> Email
                                            </div>
                                            <p className="text-sm font-semibold text-[#53545a] truncate">
                                                {selected.email}
                                            </p>
                                        </div>
                                        <div className="bg-[#919191]/5 border border-[#919191]/15 rounded-lg px-4 py-3">
                                            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-[#919191] mb-1">
                                                <FiCalendar /> Submitted
                                            </div>
                                            <p className="text-sm font-semibold text-[#53545a]">
                                                {selected.date}
                                            </p>
                                        </div>
                                        <div className="bg-[#919191]/5 border border-[#919191]/15 rounded-lg px-4 py-3">
                                            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-[#919191] mb-1">
                                                <FiClock /> Status
                                            </div>
                                            <span
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                                                style={{
                                                    background:
                                                        selected.status === 'Accepted' ? 'rgba(22,163,74,0.12)'
                                                        : selected.status === 'Rejected' ? 'rgba(220,38,38,0.12)'
                                                        : 'rgba(244,127,46,0.12)',
                                                    color:
                                                        selected.status === 'Accepted' ? '#16a34a'
                                                        : selected.status === 'Rejected' ? '#dc2626'
                                                        : '#F47F2E',
                                                }}
                                            >
                                                {selected.status}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Message */}
                                    {selected.message && (
                                        <div>
                                            <p className="text-[11px] font-semibold tracking-wider uppercase text-[#919191] mb-2">
                                                Message
                                            </p>
                                            <div className="bg-white border border-[#919191]/20 rounded-lg px-4 py-3 text-sm text-[#53545a] leading-relaxed">
                                                {selected.message}
                                            </div>
                                        </div>
                                    )}


                                    {/* Attachments */}
                                    {selected.attachments && selected.attachments.length > 0 && (
                                        <div>
                                            <p className="text-[11px] font-semibold tracking-wider uppercase text-[#919191] mb-2">
                                                Attachments
                                            </p>
                                            <ul className="space-y-2">
                                                {selected.attachments.map((file, idx) => (
                                                    <li
                                                        key={idx}
                                                        className="flex items-center justify-between bg-[#919191]/5 border border-[#919191]/15 rounded-lg px-4 py-2.5"
                                                    >
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <FiPaperclip className="text-[#0077b5] flex-shrink-0" />
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium text-[#53545a] truncate">
                                                                    {file.name}
                                                                </p>
                                                                <p className="text-xs text-[#919191]">
                                                                    {file.size}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#0077b5] text-[#0077b5] text-xs font-semibold hover:bg-[#0077b5]/10 transition-colors">
                                                            <FiDownload />
                                                            Download
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-[#919191]/20 bg-white">
                                    <button
                                        onClick={closeModal}
                                        className="px-4 py-2 text-sm font-medium text-[#53545a] border border-[#919191]/30 rounded-lg hover:bg-[#919191]/10"
                                    >
                                        Close
                                    </button>
                                    <div className="flex items-center gap-2">
                                        {isQuery ? (
                                            <>
                                                {/* <button
                                                    onClick={() => openChat(selected)}
                                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#F47F2E] text-[#F47F2E] text-sm font-semibold hover:bg-[#F47F2E]/10 transition-colors"
                                                >
                                                    <FiHeadphones />
                                                    Reply
                                                </button> */}
                                               
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => handleStatusChange(selected, 'Rejected')}
                                                    disabled={actionLoading.has(selected.id) || selected.status === 'Accepted' || selected.status === 'Rejected'}
                                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#F47F2E] text-[#F47F2E] text-sm font-semibold hover:bg-[#F47F2E]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <FiX />
                                                    Reject
                                                </button>
                                                <button
                                                    onClick={() => handleStatusChange(selected, 'Accepted')}
                                                    disabled={actionLoading.has(selected.id) || selected.status === 'Accepted' || selected.status === 'Rejected'}
                                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0077b5] text-white text-sm font-semibold hover:bg-[#005f8e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <FiCheck />
                                                    Accept
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Chat Modal (reuses existing user ChatModal) */}
            <ChatModal
                isOpen={!!chatRequest}
                onClose={() => setChatRequest(null)}
                request={chatRequest}
            />

            {/* Toast notifications */}
            <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-2 pointer-events-none">
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        className="pointer-events-auto flex items-center gap-3 pl-4 pr-3 py-3 rounded-xl shadow-lg text-sm font-semibold min-w-[260px] max-w-xs"
                        style={{
                            background: 
                                toast.type === 'success' ? '#16a34a' 
                                : toast.type === 'warning' ? '#F47F2E' 
                                : '#dc2626',
                            color: '#fff',
                        }}
                    >
                        <span className="flex-1">{toast.message}</span>
                        <button
                            onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
                            className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-white/20 transition-colors flex-shrink-0"
                            aria-label="Dismiss"
                        >
                            <FiX />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminRequests;

